/**
 * Utility functions for user-related operations
 */

// import { getUserProfile } from "../services/userProfile";

/**
 * Extract user name from Supabase auth metadata with fallback to profile table
 * @param {Object} user - Supabase auth user object
 * @param {Object} profileData - Optional profile data to avoid additional API call
 * @returns {string} User display name
 */
export const getUserDisplayName = (user, profileData = null) => {
  if (!user) return "Anonymous";

  // If profile data is provided, use it first
  if (profileData?.first_name && profileData?.last_name) {
    return `${profileData.first_name} ${profileData.last_name}`;
  }

  // Try different possible fields for user name from auth metadata
  return (
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    (user.user_metadata?.first_name && user.user_metadata?.last_name
      ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
      : null) ||
    user.email ||
    user.id ||
    "Unknown User"
  );
};

/**
 * Get user display name with profile data lookup
 * @param {Object} user - Supabase auth user object
 * @returns {Promise<string>} User display name
 */
export const getUserDisplayNameWithProfile = async (user, userProfile) => {
  if (!user) return "Anonymous";

  try {
    // Try to get profile data first

    return getUserDisplayName(user, userProfile);
  } catch (error) {
    console.warn("Failed to fetch user profile for display name:", error);
  }

  // Fallback to auth metadata only
  return getUserDisplayName(user);
};

/**
 * Get user's BSA number from profile or auth metadata
 * @param {Object} user - Supabase auth user object
 * @param {Object} profileData - Optional profile data
 * @returns {string|null} BSA number
 */
export const getUserBsaNumber = (user, profileData = null) => {
  if (!user) return null;

  return profileData?.bsa_number || user.user_metadata?.bsa_number || null;
};

/**
 * Check if user has completed their profile
 * @param {Object} user - Supabase auth user object
 * @param {Object} profileData - Optional profile data
 * @returns {boolean} True if profile is complete
 */
export const isProfileComplete = (user, profileData = null) => {
  if (!user) return false;

  const firstName = profileData?.first_name || user.user_metadata?.first_name;
  const lastName = profileData?.last_name || user.user_metadata?.last_name;
  const bsaNumber = getUserBsaNumber(user, profileData);

  return !!(firstName && lastName && bsaNumber);
};

/**
 * Get user's role from profile or auth metadata
 * @param {Object} user - Supabase auth user object
 * @param {Object} profileData - Optional profile data
 * @returns {string} User role ('user' or 'admin')
 */
export const getUserRole = (user, profileData = null) => {
  if (!user) return "user";

  return profileData?.role || user.user_metadata?.role || "user";
};

/**
 * Check if user is admin
 * @param {Object} user - Supabase auth user object
 * @param {Object} profileData - Optional profile data
 * @returns {boolean} True if user is admin
 */
export const isUserAdmin = (user, profileData = null) => {
  return getUserRole(user, profileData) === "admin";
};

/**
 * Get user role display name
 * @param {string} role - User role
 * @returns {string} Formatted role name
 */
export const getRoleDisplayName = (role) => {
  switch (role) {
    case "admin":
      return "Administrator";
    case "user":
      return "User";
    default:
      return "User";
  }
};

/**
 * Check if user can perform admin actions
 * @param {Object} user - Supabase auth user object
 * @param {Object} profileData - Optional profile data
 * @returns {boolean} True if user has admin permissions
 */
export const canPerformAdminActions = (user, profileData = null) => {
  return isUserAdmin(user, profileData);
};
